import React, { useEffect, useMemo, useState } from "react";
import {
  getProgress,
  getProgressSummary,
  getUpdateRunList,
} from "../../api/process";
import { buildWbsTree } from "../../utils/buildWbsTree";
import MobileTreeTable from "../../components/process/MobileTreeTable";
import MobileSummaryCard from "../../components/process/MobileSummaryCard";

const Progress = () => {
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [project, setProject] = useState({ name: "", summary: null });
  const [search, setSearch] = useState("");

  // ✅ Run 목록 상태
  const [actualRuns, setActualRuns] = useState([]);
  const [baselineRuns, setBaselineRuns] = useState([]);
  const [selectedActual, setSelectedActual] = useState(null);
  const [selectedBaseline, setSelectedBaseline] = useState(null);

  // ✅ 최초 실행: Run 목록 불러오기
  useEffect(() => {
    const loadRuns = async () => {
      try {
        const runs = await getUpdateRunList();
        console.log("✅ Run 목록:", runs);

        const actual = runs.filter((r) => r.run_type === "A");
        const baseline = runs.filter((r) => r.run_type === "B");

        setActualRuns(actual);
        setBaselineRuns(baseline);

        // 최신 기준으로 기본 선택
        const latestActual = actual.at(0);
        const latestBaseline = baseline.at(0);

        setSelectedActual(latestActual?.id || null);
        setSelectedBaseline(latestBaseline?.id || null);
      } catch (err) {
        console.error("❌ Run 목록 불러오기 실패:", err);
      }
    };

    loadRuns();
  }, []);

  // ✅ 선택 변경 시 데이터 불러오기
  useEffect(() => {
    if (!selectedActual) {
      setLoading(false);
      return;
    }

    const fetchProgress = async () => {
      try {
        setLoading(true);
        console.log("Fetching progress for:", selectedActual, selectedBaseline);

        // 활동 데이터
        const data = await getProgress(selectedActual, selectedBaseline);
        setTreeData(buildWbsTree(data.activities));

        // 요약 데이터
        const summaryData = await getProgressSummary(
          selectedActual,
          selectedBaseline
        );
        console.log(summaryData);

        setProject({
          ...data.project,
          ...summaryData?.project,
          summary: summaryData?.project?.summary || null,
        });

        console.log("✅ Progress + Summary loaded");
      } catch (err) {
        console.error("❌ Progress load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [selectedActual, selectedBaseline]);

  // ✅ 검색 필터링 (CP/완료 필터는 MobileTreeTable에서 처리)
  const filteredTree = useMemo(() => {
    if (!search) return treeData;
    const lower = search.toLowerCase();

    const filterNode = (node) => {
      const acts = (node.activities || []).filter((a) =>
        a.name.toLowerCase().includes(lower) ||
        a.activity_code.toLowerCase().includes(lower)
      );
      const children = (node.children || []).map(filterNode).filter(Boolean);

      if (node.name.toLowerCase().includes(lower)) return node;
      if (acts.length > 0 || children.length > 0)
        return { ...node, activities: acts, children };
      return null;
    };

    return treeData.map(filterNode).filter(Boolean);
  }, [treeData, search]);

  // ✅ 진척률 계산
  const { totalActual, totalPlan } = useMemo(() => {
    let plan = 0,
      actual = 0;
    const collect = (node) => {
      (node.activities || []).forEach((a) => {
        plan += Number(a.plan_labor) || 0;
        actual += Number(a.actual_labor) || 0;
      });
      (node.children || []).forEach(collect);
    };
    filteredTree.forEach(collect);
    return { totalPlan: plan, totalActual: actual };
  }, [filteredTree]);

  const progressRate =
    totalPlan > 0 ? ((totalActual / totalPlan) * 100).toFixed(2) : 0;

  // ✅ 초기 로딩 화면 (기존 로직 유지)
  if (loading && !project.name) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner space-y-3 p-4 sm:p-6 rounded-2xl bg-gray-50">
          <div className="bg-white rounded-xl p-6 text-center text-gray-500 shadow-sm">
            📊 데이터 로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더: 프로젝트 정보 */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {project.name || "프로젝트"}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                업데이트:{" "}
                {project.last_updated
                  ? new Date(project.last_updated).toLocaleDateString("ko-KR")
                  : "-"}
              </p>
            </div>
          </div>

          {/* Run 선택 - 세련된 세그먼트 스타일 */}
          {actualRuns.length > 0 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {/* 실적 기준일 */}
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                    실적 기준일
                  </label>
                  <select
                    value={selectedActual || ""}
                    onChange={(e) => setSelectedActual(e.target.value)}
                    className="w-full bg-gray-50 border-0 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 transition-all"
                  >
                    {actualRuns.map((r) => (
                      <option key={r.id} value={r.id}>
                        {new Date(r.data_date).toLocaleDateString("ko-KR")}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 베이스라인 */}
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                    베이스라인
                  </label>
                  <select
                    value={selectedBaseline || ""}
                    onChange={(e) => setSelectedBaseline(e.target.value)}
                    className="w-full bg-gray-50 border-0 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-indigo-500 transition-all"
                  >
                    {baselineRuns.map((r) => (
                      <option key={r.id} value={r.id}>
                        Rev.{r.revision || "Unnamed"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 요약 카드 */}
        {project?.summary ? (
          <MobileSummaryCard summary={project.summary} />
        ) : (
          <div className="bg-slate-800 rounded-2xl p-6 text-center">
            <div className="animate-pulse flex flex-col items-center gap-2">
              <div className="w-16 h-16 bg-slate-700 rounded-full" />
              <div className="h-3 w-24 bg-slate-700 rounded" />
            </div>
          </div>
        )}

        {/* 검색 영역 */}
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 공정명 또는 코드 검색..."
            className="w-full bg-white border-0 rounded-xl px-4 py-3 text-sm shadow-sm focus:ring-2 focus:ring-blue-500 transition-all placeholder:text-gray-400"
          />
          {search && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
              {progressRate}%
            </div>
          )}
        </div>

        {/* 본문: 모바일 트리 카드 */}
        {loading ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm">데이터 로딩 중...</p>
            </div>
          </div>
        ) : (
          <MobileTreeTable treeData={filteredTree} />
        )}
      </div>
    </div>
  );
};

export default Progress;
